import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface EditModeContextValue {
  isEditMode: boolean;
  toggleEditMode: () => void;
  targetGroupId: string | null;
  setTargetGroupId: (id: string | null) => void;
  // Global popover control - only one popover can be open at a time
  activePopoverPath: string | null;
  openPopover: (path: string) => void;
  closePopover: () => void;
}

const EditModeContext = createContext<EditModeContextValue | null>(null);

interface EditModeProviderProps {
  children: ReactNode;
}

export function EditModeProvider({ children }: EditModeProviderProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [targetGroupId, setTargetGroupId] = useState<string | null>(null);
  const [activePopoverPath, setActivePopoverPath] = useState<string | null>(null);

  const toggleEditMode = useCallback(() => {
    setIsEditMode(prev => {
      if (prev) {
        // Exiting edit mode - clear selections
        setTargetGroupId(null);
        setActivePopoverPath(null);
      }
      return !prev;
    });
  }, []);

  const openPopover = useCallback((path: string) => {
    setActivePopoverPath(path);
  }, []);

  const closePopover = useCallback(() => {
    setActivePopoverPath(null);
  }, []);

  return (
    <EditModeContext.Provider
      value={{
        isEditMode,
        toggleEditMode,
        targetGroupId,
        setTargetGroupId,
        activePopoverPath,
        openPopover,
        closePopover,
      }}
    >
      {children}
    </EditModeContext.Provider>
  );
}

export function useEditMode() {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }
  return context;
}

export default EditModeContext;
